import { ReactElement, useEffect, useState } from "react"
import { useNavigate } from "react-router-dom"
import Select from "react-select"
import BaseTable, { AutoResizer, Column, ColumnShape } from "react-base-table"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { FetchBaseQueryError } from "@reduxjs/toolkit/dist/query"
import { Modal, Pagination, Tag, Loading, ContextMenu, ContextMenuItem, Search, Button } from "@wfp/ui"
import {
  faBan,
  faEye,
  faThumbsUp,
  faTrash,
  faEyeSlash,
  faRotateLeft,
  faPenToSquare,
  IconDefinition,
  faSquareCaretDown,
} from "@fortawesome/free-solid-svg-icons"
import { iconAddOutline, iconSubtractOutline } from "@wfp/icons"
import { CONTRIBUTOR_ROLE, GET_ITEM_LIMIT, SurveyStatus } from "constants/constants"
import { permissionsHH } from "constants/household"
import { SimpleSelect, Survey, SurveyHHFilters } from "interfaces/dataInterfaces"
import {
  useGetSurveyHHListQuery,
  useDeleteSurveyHHMutation,
  useUpdateStatusHHMutation,
  useUpdateVisibilityHHMutation,
  useGetVisibilitiesQuery,
} from "services/household/survey"
import useCommon from "hooks/useCommon"
import { useAppSelector } from "hooks/hooksStore"
import { selectUser } from "features/common/commonSlice"
import { checkAccess, formatDate } from "utils/helpers"
import PermissionWrapper from "components/Shared/Common/PermissionWrapper"
import DeleteConfirmationModal from "components/Shared/ConfirmationModal/DeleteConfirmationModal"
import CountrySelector from "components/Shared/HeaderFilters/AssociatedCountriesSelector"
import HeaderFilters from "components/Shared/HeaderFilters/HeaderFilters"
import HeaderCTA from "components/Shared/HeaderFilters/HeaderCTA"
import AdvancedFilters from "./AdvancedFilters"

interface IContextItem {
  onAction: () => void
  feature: string
  label: string
  icon: IconDefinition
  color: string
  exception?: boolean
}

interface IActionItem<T> {
  onAction: (data: T) => void
  feature: string
  label: string
  icon: IconDefinition
  color: string
  visible: number[]
  exception?: (data: T) => boolean | boolean
}

type ActionTypes = "details" | "delete" | "edit" | "approve" | "reject" | "unstage" | "review" | "unreview"

const tagType: any = {
  1: "warning",
  2: "info",
  3: "success",
  4: "error",
}

const defaultValues: SurveyHHFilters = {
  organizationIds: [],
  surveyCategoryIds: [],
  surveySubCategoryIds: [],
  surveyModalityIds: [],
  surveyAttributeIds: [],
  surveyAssessmentStatusIds: [],
  surveyPhaseIds: [],
  startDate: null,
  endDate: null,
  isContinuousMonitoring: false,
}

const SurveyPage = (): ReactElement => {
  const navigate = useNavigate()
  const { showSnackBar } = useCommon()
  const user = useAppSelector(selectUser)

  const [page, setPage] = useState<number>(1)
  const [filtersExpanded, setFiltersExpanded] = useState(false)
  const [pageSize, setPageSize] = useState<number>(GET_ITEM_LIMIT)
  const [values, setValues] = useState({ ...defaultValues })
  const [applyFilterValues, setApplyFilterValues] = useState({ ...defaultValues })
  const [actionItem, setActionItem] = useState<{ item: Survey; action: ActionTypes } | null>(null)
  const [admCode, setAdmCode] = useState(null)
  const [query, setQuery] = useState("")
  const [searchValue, setSearchValue] = useState<string>("")
  const [visibility, setVisibility] = useState({ value: "", label: "" })
  const { data: surveyData, isFetching: isLoading } = useGetSurveyHHListQuery({
    query: {
      adm0Code: admCode ?? 0,
      search: query,
      skip: page === 1 ? 0 : (page - 1) * pageSize,
      top: pageSize,
    },
    body: { ...applyFilterValues },
  })

  const [deleteSurvey] = useDeleteSurveyHHMutation()
  const [updateStatus] = useUpdateStatusHHMutation()
  const [updateVisibility] = useUpdateVisibilityHHMutation()
  const { data: visibilities } = useGetVisibilitiesQuery()

  const actionsMenu: IActionItem<Survey>[] = [
    {
      label: "Details",
      icon: faEye,
      color: "var(--interactive-01)",
      feature: "details",
      onAction: (rowData: Survey) => navigate(`/household/surveys/${rowData.surveyId}#details`),
      visible: [1, 2, 3, 4],
    },
    {
      label: "Edit",
      icon: faPenToSquare,
      color: "var(--interactive-01)",
      feature: "edit",
      exception: (rowData: Survey) =>
        user?.roles?.find(role => role.applicationModuleId === permissionsHH.moduleId)?.roleName === CONTRIBUTOR_ROLE &&
        user?.nameid?.toLowerCase() === rowData?.userName?.toLowerCase(),
      onAction: (rowData: Survey) => navigate(`/household/surveys/${rowData?.surveyId}?mode=edit#details`),
      visible: [1],
    },
    {
      label: "Review",
      icon: faEye,
      color: "var(--support-02)",
      feature: "approve",
      onAction: (rowData: Survey) => setActionItem({ item: rowData, action: "review" }),
      visible: [1],
    },
    {
      label: "Unstage",
      icon: faRotateLeft,
      color: "var(--support-01)",
      feature: "approve",
      onAction: (rowData: Survey) => setActionItem({ item: rowData, action: "unstage" }),
      visible: [3, 4],
    },
    {
      label: "Unreview",
      icon: faEyeSlash,
      color: "var(--support-01)",
      feature: "approve",
      onAction: (rowData: Survey) => setActionItem({ item: rowData, action: "unreview" }),
      visible: [2],
    },
    {
      label: "Approve",
      icon: faThumbsUp,
      color: "var(--support-02)",
      feature: "approve",
      onAction: (rowData: Survey) => setActionItem({ item: rowData, action: "approve" }),
      visible: [2],
    },
    {
      label: "Reject",
      icon: faBan,
      color: "var(--support-01)",
      feature: "reject",
      onAction: (rowData: Survey) => setActionItem({ item: rowData, action: "reject" }),
      visible: [2],
    },
    {
      label: "Delete",
      icon: faTrash,
      color: "var(--support-01)",
      feature: "delete",
      onAction: (rowData: Survey) => setActionItem({ item: rowData, action: "delete" }),
      visible: [2],
    },
  ]

  const RenderContextItem = ({ onAction, feature, label, icon, color, exception }: IContextItem) => {
    return (
      <PermissionWrapper {...permissionsHH} feature={feature} exception={exception}>
        <ContextMenuItem className="hover:bg-slate-200 p-1.5 px-2 text-left w-28" onClick={onAction}>
          <div>
            <FontAwesomeIcon icon={icon} color={color} className="mr-1" />
            <span style={{ color }}>{label}</span>
          </div>
        </ContextMenuItem>
      </PermissionWrapper>
    )
  }

  const columns: Array<ColumnShape<Survey>> = [
    {
      key: "surveyId",
      dataKey: "surveyId",
      title: "ID",
      width: 65,
      maxWidth: 65,
    },
    {
      key: "surveyStatusId",
      dataKey: "surveyStatusId",
      title: "Status",
      width: 110,
      cellRenderer: ({ rowData }) => (
        <Tag type={tagType[rowData.surveyStatusId] || ""} className="font-bold">
          {rowData.surveyStatusName || ""}
        </Tag>
      ),
    },
    {
      key: "surveyName",
      dataKey: "surveyName",
      title: "Name",
      width: 450,
      resizable: true,
      cellRenderer: ({ rowData }) => (
        <div className="w-full">
          <div className="font-bold truncate">{rowData.surveyName || ""}</div>
          <div className="truncate" style={{ marginTop: "0.3rem" }}>
            {rowData.xlsFormName || ""}
          </div>
        </div>
      ),
    },
    {
      key: "countryName",
      dataKey: "countryName",
      title: "Country",
      width: 290,
      resizable: true,
    },
    {
      key: "surveyCreateDate",
      title: "Created date",
      width: 115,
      cellRenderer: ({ rowData }) => formatDate(rowData.surveyCreateDate),
    },
    {
      key: "userName",
      dataKey: "userName",
      title: "Create By",
      width: 280,
      resizable: true,
    },
    {
      key: "approvedBy",
      dataKey: "approvedBy",
      title: "Approved by",
      width: 280,
      resizable: true,
    },
    {
      key: "action",
      width: 100,
      title: "Actions",
      align: Column.Alignment.CENTER,
      hidden: !checkAccess(user?.roles, [permissionsHH.moduleId], permissionsHH.subModule, "all"),
      cellRenderer: ({ rowData }) => (
        <div
          style={{
            minWidth: "2rem",
            minHeight: "2rem",
            borderRadius: "50%",
          }}
          onClick={e => {
            e.preventDefault()
            e.stopPropagation()
          }}
        >
          <ContextMenu
            content={actionsMenu.map(
              item =>
                item.visible.includes(rowData.surveyStatusId) && (
                  <RenderContextItem
                    key={item.label}
                    label={item.label}
                    feature={item.feature}
                    icon={item.icon}
                    color={item.color}
                    onAction={() => item.onAction(rowData)}
                    exception={item.exception && item.exception(rowData)}
                  />
                ),
            )}
          >
            <div style={{ padding: "0.5rem" }}>
              <FontAwesomeIcon icon={faSquareCaretDown} color="var(--interactive-01)" size="xl" />
            </div>
          </ContextMenu>
        </div>
      ),
    },
  ]

  const handleSearch = (): void => {
    setQuery(searchValue)
    setPage(1)
  }

  const handleFilters = (): void => {
    setApplyFilterValues({ ...values })
    setPage(1)
  }

  const getData = (event?: SimpleSelect): void => {
    setAdmCode(event?.value || 0)
    setPage(1)
  }

  const handleUpdateStatus = async ({ surveyId, statusId }: { surveyId: number; statusId: number }): Promise<void> => {
    try {
      await updateStatus({ surveyId, statusId }).unwrap()
      showSnackBar("Survey status updated successfully", "success")
    } catch (err) {
      const error = err as FetchBaseQueryError
      showSnackBar(`Failed to update survey status: ${(error?.data as { detail: string })?.detail}`, "error")
    }
  }

  useEffect(() => {
    const updateActionItem =
      actionItem &&
      (actionItem?.action === "reject" ||
        actionItem?.action === "unstage" ||
        actionItem?.action === "review" ||
        actionItem?.action === "unreview")

    if (!updateActionItem) return
    switch (actionItem?.action) {
      case "reject":
        handleUpdateStatus({ surveyId: actionItem.item.surveyId, statusId: SurveyStatus.REJECT })
        break
      case "unstage":
      case "unreview":
        handleUpdateStatus({ surveyId: actionItem.item.surveyId, statusId: SurveyStatus.IN_PROGRESS })
        break
      default:
        handleUpdateStatus({ surveyId: actionItem.item.surveyId, statusId: SurveyStatus.PENDING })
        break
    }
    setActionItem(null)
  }, [actionItem])

  return (
    <>
      <Loading active={isLoading} small />
      <HeaderFilters
        leftWidth="w-9/12"
        left={
          <>
            <CountrySelector onSelect={getData} className="w-5/12 mr-2" />
            <Search
              id="search"
              onChange={e => setSearchValue(e.toString())}
              className="mr-1"
              style={{ borderRadius: 5 }}
              name="input-name"
              // @ts-ignore
              closeButtonLabelText="Clear search input"
            />
            <Button onClick={handleSearch}>Search</Button>
            <Button
              onClick={() => setFiltersExpanded(!filtersExpanded)}
              kind="secondary"
              icon={filtersExpanded ? iconSubtractOutline : iconAddOutline}
              className="ml-2"
              iconDescription="Expand or collapse filters"
            >
              {filtersExpanded ? "Hide Filters" : "Advanced Filters"}
            </Button>
          </>
        }
        right={
          <PermissionWrapper {...permissionsHH} feature="addNew">
            <HeaderCTA onClick={() => navigate("add")} label="Add New Survey" />
          </PermissionWrapper>
        }
      />

      {filtersExpanded && (
        <AdvancedFilters
          onApply={handleFilters}
          onClear={() => {
            setApplyFilterValues({ ...defaultValues })
            setValues({ ...defaultValues })
            setFiltersExpanded(false)
          }}
          values={values}
          setValues={setValues}
        />
      )}

      <div style={{ height: "calc(100vh - 225px)", minHeight: "60vh", marginTop: "10px" }}>
        <AutoResizer>
          {({ width, height }) => (
            <BaseTable
              data={surveyData?.items}
              width={width}
              height={height}
              rowKey="surveyId"
              className="custom-table clickable"
              rowHeight={50}
              headerHeight={45}
              columns={columns}
              emptyRenderer={() => <p>No records to display</p>}
              rowEventHandlers={{
                onClick: row => {
                  navigate(`/Household/Surveys/${row.rowKey}#details`)
                },
              }}
            />
          )}
        </AutoResizer>
      </div>

      <DeleteConfirmationModal
        lazyLoad
        danger
        open={actionItem?.action === "delete"}
        onRequestClose={() => setActionItem(null)}
        onRequestSubmit={() => {
          deleteSurvey(actionItem?.item.surveyId as number)
          setActionItem(null)
        }}
      />

      <Modal
        open={actionItem?.action === "approve"}
        modalHeading="Set visibility"
        primaryButtonText="Save"
        onRequestClose={() => setActionItem(null)}
        onRequestSubmit={() => {
          if (!visibility?.value || !actionItem) return
          updateVisibility({ surveyId: actionItem.item.surveyId, visibilityId: Number(visibility.value) })
          handleUpdateStatus({ surveyId: actionItem.item.surveyId, statusId: SurveyStatus.APPROVE })
          setActionItem(null)
        }}
      >
        <Select
          isSearchable
          placeholder="Select visibility"
          onChange={(e: any) => setVisibility(e)}
          options={visibilities?.map(v => ({ ...v, label: v.name }))}
        />
      </Modal>

      <Pagination
        totalItems={surveyData?.count ?? 0}
        pageSize={pageSize}
        pageSizes={[10, 20, 30]}
        page={page}
        onChange={event => {
          const item = event as unknown as { page: number; pageSize: number }
          setPage(item.page)
          setPageSize(item.pageSize)
        }}
      />
    </>
  )
}

export default SurveyPage
